import {gql} from '@apollo/client';
import {groupByUniqueKey} from '@benchling/js-utils';
import {useEffect, useRef, useMemo} from 'react';
import areEqual from 'react-fast-compare';
import {useSelector} from 'react-redux';
import {debounce} from 'ts-debounce';

import * as analytics from 'analytics/Analytics';
import isArchived from 'archive/isArchived';
import {MolbioFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {AssemblyCoordinator} from 'molbio/bulk-assembly/AssemblyCoordinator';
import {
  BulkAssemblyFileQuery,
  BulkAssemblyFileQueryVariables,
} from 'molbio/bulk-assembly/backbone/__generated/BulkAssemblyFileQuery';
import {BULK_ASSEMBLY_FILE_QUERY} from 'molbio/bulk-assembly/backbone/BulkAssemblyFileState';
import {
  bulkAssemblyInitialized,
  startBulkAssemblyAsyncValidation,
} from 'molbio/bulk-assembly/BulkAssemblyActions';
import {startBulkAssemblyAutoSaverIfNecessary} from 'molbio/bulk-assembly/BulkAssemblyAutoSaver';
import {
  BulkAssemblyGQL,
  BulkAssemblyProcedure,
  CLIENT_PROCEDURE_JSON_CURRENT_VERSION,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {asyncValidateAssembly} from 'molbio/bulk-assembly/BulkAssemblyDAO';
import BulkAssemblyPolymerData, {
  useBulkAssemblyPolymerData,
} from 'molbio/bulk-assembly/BulkAssemblyPolymerData';
import {
  BulkAssemblyValidationCache,
  makeBulkAssemblyValidationCache,
  prepareValidationResults,
  shouldSkipBulkAssemblyValidation,
} from 'molbio/bulk-assembly/BulkAssemblyValidation';
import {
  AsyncBulkAssemblyValidationResults,
  ValidationStatus,
  ProcessedAsyncBulkAssemblyValidationResults,
  BulkAssemblyConstructValidationResult,
  BulkAssemblyFragmentValidationResult,
  BulkAsesmblyAssemblyValidationResult,
} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import BulkAssemblyStateManager from 'molbio/bulk-assembly/state/BulkAssemblyStateManager';
import notifications from 'notifications';
import {BenchlingState} from 'redux-stores/reduceApp';
import {getStore} from 'redux-stores/Store';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import {LOADING} from 'util/Types';

export const ASYNC_VALIDATION_DEBOUNCE_TIME = 1000;

export const BULK_ASSEMBLY_VALIDATION_RESULTS_QUERY = gql`
  query BulkAssemblyValidationResultsQuery($procedureJson: JSONScalar!)
  @skipAudit
  @engComponent(name: MOLBIO) {
    bulkAssemblyValidationJson(procedureJson: $procedureJson)
  }
`;

/**
 * Determine the procedure JSON (i.e. state) to use for rendering, and initialize Redux if applicable.
 *
 * For in-progress assemblies, Redux keeps the source of truth for the state, and the server-provided
 * bulk assembly gives the *initial* state.
 * For finalized assemblies, the state is served from the bulk assembly object directly without using
 * Redux. This also makes it so any attempt to update Redux (e.g. due to a bug where we aren't disabling
 * editing) gives an assertAndContinue and does nothing.
 *
 * Returns null in the case where we don't get bulk assembly information back or we are still in the
 * process of initializing Redux.
 */
function useAssemblyProcedure(
  assemblyId: string,
  bulkAssembly: BulkAssemblyGQL | null
): BulkAssemblyProcedure | null {
  const reduxState =
    useSelector((state: BenchlingState) => state.ui.molbio.bulkAssemblies[assemblyId]) ?? null;
  if (bulkAssembly != null && bulkAssembly.procedureJson == null) {
    // Sentinel indicates error.
    notifications.error('There was an error loading data. Please refresh the page');
  }
  const serverProcedure: BulkAssemblyProcedure | null = bulkAssembly?.procedureJson ?? null;

  const isFinalized = bulkAssembly?.isFinalized ?? false;
  const isBulkAssemblyArchived = bulkAssembly != null && isArchived(bulkAssembly);
  const canEditBulkAssembly =
    bulkAssembly?.permissions.writable && isFeatureEnabled({feature: MolbioFeature.MOLBIO_EDIT});
  const reduxIsInitialized = reduxState != null;
  useEffect(() => {
    if (
      !isFinalized &&
      !isBulkAssemblyArchived &&
      canEditBulkAssembly &&
      serverProcedure != null &&
      !reduxIsInitialized
    ) {
      getStore().dispatch(bulkAssemblyInitialized(assemblyId, serverProcedure));
    }
  }, [
    isFinalized,
    isBulkAssemblyArchived,
    canEditBulkAssembly,
    assemblyId,
    reduxIsInitialized,
    serverProcedure,
  ]);

  // Archiving doesn't update the assembly procedure, so we want to take the redux procedure if we have one
  // (i.e. if the page has not been refreshed since the archive is performed)
  if (isFinalized || !canEditBulkAssembly || (isBulkAssemblyArchived && !reduxIsInitialized)) {
    return serverProcedure;
  } else {
    return reduxState == null ? null : reduxState.procedure;
  }
}

function trackBulkAssemblyValidationAnalyticsEvent(
  bulkAssemblyId: string,
  validationResults: ProcessedAsyncBulkAssemblyValidationResults
): void {
  analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.UPDATED_BULK_ASSEMBLY_VALIDATION, {
    actionOrigin: ActionOrigin.MOLBIO,
    assemblyId: bulkAssemblyId,
    numAssemblyWarnings: getNumValidationResultsByStatus(
      [validationResults.assemblyResult],
      ValidationStatus.WARNING
    ),
    numAssemblyErrors: getNumValidationResultsByStatus(
      [validationResults.assemblyResult],
      ValidationStatus.ERROR
    ),
    numAssemblySuccesses: getNumValidationResultsByStatus(
      [validationResults.assemblyResult],
      ValidationStatus.SUCCESS
    ),
    numFragmentWarnings: getNumValidationResultsByStatus(
      Object.values(validationResults.fragmentResultsByCid),
      ValidationStatus.WARNING
    ),
    numFragmentErrors: getNumValidationResultsByStatus(
      Object.values(validationResults.fragmentResultsByCid),
      ValidationStatus.ERROR
    ),
    numFragmentSuccesses: getNumValidationResultsByStatus(
      Object.values(validationResults.fragmentResultsByCid),
      ValidationStatus.SUCCESS
    ),
    numFragmentRows: Object.values(validationResults.fragmentResultsByCid).length,
    numCompletedFragmentRows: Object.values(validationResults.fragmentResultsByCid).filter(
      (result) => result != null
    ).length,
    numConstructWarnings: getNumValidationResultsByStatus(
      Object.values(validationResults.constructResultsByCid),
      ValidationStatus.WARNING
    ),
    numConstructErrors: getNumValidationResultsByStatus(
      Object.values(validationResults.constructResultsByCid),
      ValidationStatus.ERROR
    ),
    numConstructSuccesses: getNumValidationResultsByStatus(
      Object.values(validationResults.constructResultsByCid),
      ValidationStatus.ERROR
    ),
    numConstructRows: Object.values(validationResults.constructResultsByCid).length,
    numCompletedConstructRows: Object.values(validationResults.constructResultsByCid).filter(
      (result) => result != null
    ).length,
  });
}

export const NEW_VALIDATION_REQUEST_ERROR_MESSAGE = 'new validation request';

/**
 * Get the validation and preview results for this assembly.
 *
 * For in-progress assemblies, we determine inputs to the validation endpoint and call it.
 * For finalized assemblies, we take the validation and other results directly from the
 * assembly object.
 */
export function useAssemblyValidationResults(
  state: BulkAssemblyProcedure | null,
  bulkAssembly: BulkAssemblyGQL | null,
  bulkAssemblyPolymerData: BulkAssemblyPolymerData
): {
  validationResults: ProcessedAsyncBulkAssemblyValidationResults;
  refetchValidation: () => Promise<unknown>;
} {
  // Store granular validation results in the non-finalized case so we can serve the information we
  // know while the validation query as a whole is in a loading state.
  const validationCache = useRef<BulkAssemblyValidationCache>(makeBulkAssemblyValidationCache());

  const isBulkAssemblyArchived = bulkAssembly != null && isArchived(bulkAssembly);
  const isFinalized = bulkAssembly && bulkAssembly.isFinalized;

  const shouldSkipValidation =
    isBulkAssemblyArchived ||
    isFinalized ||
    state == null ||
    shouldSkipBulkAssemblyValidation(state, bulkAssemblyPolymerData);

  const asyncState = useSelector((state: BenchlingState) =>
    bulkAssembly == null ? null : state.ui.molbio.bulkAssemblies[bulkAssembly.id]?.asyncState
  );

  const debouncedAsyncValidateAssembly = useMemo(
    () => debounce(asyncValidateAssembly, ASYNC_VALIDATION_DEBOUNCE_TIME),
    []
  );
  const asyncRefetch: () => Promise<void> = () =>
    new Promise((resolve) => {
      if (bulkAssembly != null && state != null) {
        resolve(startBulkAssemblyAsyncValidation(bulkAssembly.id, state, debouncedAsyncValidateAssembly));
      } else {
        notifications.error(
          'There was an error finalizing your assembly. Consider refreshing the page in case the assembly has gotten out of date.'
        );
        resolve();
      }
    });
  const refetchValidation = asyncRefetch;

  const shouldDoAsyncValidation =
    bulkAssembly != null &&
    state != null &&
    (asyncState?.status === AsyncStatus.NOT_FETCHED ||
      (asyncState != null &&
        asyncState.status !== AsyncStatus.ERROR &&
        !areEqual(asyncState.input, state))) &&
    !shouldSkipValidation;

  useEffect(() => {
    if (shouldDoAsyncValidation) {
      startBulkAssemblyAsyncValidation(bulkAssembly.id, state, debouncedAsyncValidateAssembly);
    }
  }, [shouldDoAsyncValidation, bulkAssembly, state, debouncedAsyncValidateAssembly]);

  let validationResults: ProcessedAsyncBulkAssemblyValidationResults;
  if (isFinalized) {
    const {assemblyValidationResult, fragmentValidationResults, constructValidationResults} =
      bulkAssembly.finalizedResultsJson as AsyncBulkAssemblyValidationResults;
    validationResults = {
      assemblyResult: assemblyValidationResult,
      fragmentResultsByCid: groupByUniqueKey(fragmentValidationResults, (result) => result.fragmentId),
      constructResultsByCid: groupByUniqueKey(constructValidationResults, (result) => result.constructId),
    };
  } else {
    validationResults = prepareValidationResults(
      shouldSkipValidation
        ? null
        : asyncState != null && asyncState.status === AsyncStatus.SUCCESS && asyncState.input != null
          ? asyncState.input
          : state,
      asyncState != null && asyncState.status === AsyncStatus.SUCCESS && asyncState.data != null
        ? asyncState.data
        : null,
      validationCache.current
    );
    if (bulkAssembly != null && !isValidationLoading(validationResults)) {
      trackBulkAssemblyValidationAnalyticsEvent(bulkAssembly.id, validationResults);
    }
  }
  return {validationResults, refetchValidation};
}

function isValidationLoading(
  bulkAssemblyValidationResults: ProcessedAsyncBulkAssemblyValidationResults
): boolean {
  return (
    bulkAssemblyValidationResults.assemblyResult === LOADING ||
    Object.values(bulkAssemblyValidationResults.fragmentResultsByCid).includes(LOADING) ||
    Object.values(bulkAssemblyValidationResults.constructResultsByCid).includes(LOADING)
  );
}

function getNumValidationResultsByStatus(
  validationResults: Array<
    | BulkAsesmblyAssemblyValidationResult
    | BulkAssemblyFragmentValidationResult
    | BulkAssemblyConstructValidationResult
    | LOADING
    | null
    | undefined
  >,
  status: ValidationStatus
): number {
  return validationResults
    .flatMap((result) => (result != null && result !== LOADING ? result.validationMessages : []))
    .filter((message) => message.status === status).length;
}

/**
 * Initialize an AssemblyCoordinator, which holds all data needed for a render of the bulk assembly
 * UI and is able to calculate all relevant derived values.
 *
 * All information needed for render is sourced from Redux and a handful of GraphQL queries that get
 * the bulk assembly itself, relevant sequence information, and relevant validation information.
 */
export function useAssemblyCoordinator(assemblyId: string): AssemblyCoordinator | null {
  const bulkAssemblyResult = useBenchlingQuery<BulkAssemblyFileQuery, BulkAssemblyFileQueryVariables>(
    BULK_ASSEMBLY_FILE_QUERY,
    {variables: {fileId: assemblyId, procedureVersion: CLIENT_PROCEDURE_JSON_CURRENT_VERSION}}
  );
  const bulkAssembly: BulkAssemblyGQL | null =
    bulkAssemblyResult.type === QueryResultType.SUCCESS &&
    !isInaccessible(bulkAssemblyResult.data.bulkAssembly)
      ? bulkAssemblyResult.data.bulkAssembly
      : null;

  const state = useAssemblyProcedure(assemblyId, bulkAssembly);
  const polymerData = useBulkAssemblyPolymerData(state);
  const {validationResults, refetchValidation} = useAssemblyValidationResults(
    state,
    bulkAssembly,
    polymerData
  );
  const bulkAssemblyState = useSelector((state: BenchlingState) =>
    bulkAssembly == null ? null : state.ui.molbio.bulkAssemblies[bulkAssembly.id]
  );

  const assemblyCoordinator =
    bulkAssembly != null && state != null
      ? new AssemblyCoordinator(
          new BulkAssemblyStateManager(state, polymerData),
          assemblyId,
          bulkAssembly.isFinalized,
          isArchived(bulkAssembly),
          bulkAssembly.permissions,
          state,
          bulkAssembly.name,
          bulkAssembly.folder.id,
          polymerData,
          validationResults,
          bulkAssemblyResult.refetch,
          refetchValidation,
          bulkAssemblyState?.fragmentStatusFilter ?? null,
          bulkAssemblyState?.constructStatusFilter ?? null
        )
      : null;
  useEffect(() => {
    if (bulkAssembly && !bulkAssembly.isFinalized) {
      startBulkAssemblyAutoSaverIfNecessary(assemblyId);
    }
    if (
      assemblyCoordinator != null &&
      bulkAssembly &&
      !isArchived(bulkAssembly) &&
      !bulkAssembly.isFinalized
    ) {
      assemblyCoordinator.performEffectsAfterRender();
    }
  });
  return assemblyCoordinator;
}
